// 蛋白质基础分析组件
import MolecularWeightResult from './protein/basic/MolecularWeightResult';
import InstabilityIndexResult from './protein/basic/InstabilityIndexResult';
import FlexibilityResult from './protein/basic/FlexibilityResult';
import ChargeProfileResult from './protein/basic/ChargeProfileResult';
import ExtinctionCoefficientResult from './protein/basic/ExtinctionCoefficientResult';
import HydropathyResult from './protein/basic/HydropathyResult';

// 蛋白质高级分析组件
import TMPredictionResult from './protein/advanced/TMPredictionResult';
import PTMPredictionResult from './protein/advanced/PTMPredictionResult';
import DisorderPredictionResult from './protein/advanced/DisorderPredictionResult';
import SignalPeptideResult from './protein/advanced/SignalPeptideResult';
import EcoliExpressionResult from './protein/advanced/EcoliExpressionResult';

// 蛋白质结构预测组件
import MolStarViewer from './protein/structure/MolStarViewer';

// 蛋白质设计组件
import ProteinMPNNResults from './protein/ProteinMPNNResults';
import ThermoMPNNResult from './structure/ThermoMPNNResult';
import ProtenixResult from './structure/ProtenixResult';
import ESMFoldResult from './structure/ESMFoldResult';

// 基因合成组件
import GeneSynthesisResult from './cloning/GeneSynthesisResult';
// 引物设计组件
import PrimerDesignResult from './cloning/PrimerDesignResult';

import BatchAntibodyResult from './antibody/BatchAntibodyResult';
import EpitopePredictionResult from './antibody/EpitopePredictionResult';
import CLBTopeResult from './antibody/CLBTopeResult';
import AntibodyStructureResult from './antibody/AntibodyStructureResult';
import ViscosityResult from './antibody/ViscosityResult';
// 序列处理组件
import SequenceConversionResult from './dnasequence/SequenceConversionResult';
import AminoAcidConversionResult from './protein/basic/AminoAcidConversionResult';
import ProteinBasicAnalysisResult from './protein/basic/ProteinBasicAnalysisResult';
import SixFrameTranslationResult from './dnasequence/SixFrameTranslationResult';
import SequenceValidationResult from './dnasequence/SequenceValidationResult';
import BatchSequenceResult from './dnasequence/BatchSequenceResult';

// 系统发育分析组件
import PhylogeneticTreeResult from './phylogenetics/PhylogeneticTreeResult';
import MultipleSequenceAlignmentResult from './phylogenetics/MultipleSequenceAlignmentResult';

// 酶分析组件
import CutMap from './enzyme/CutMap';

// 分子互作组件
import RNAhybridResult from './interaction/RNAhybridResult';
import ProdigyResult from './interaction/ProdigyResult';
import ZdockResult from './interaction/ZdockResult';
import MegadockResult from './interaction/MegadockResult';
import EquiDockResult from './interaction/EquiDockResult';

// 图表组件
import BarChart from './charts/BarChart';
import BaseChart from './charts/BaseChart';
import LineChart from './charts/LineChart';
import ScatterChart from './charts/ScatterChart';





// 组件元数据接口
interface ComponentMetadata {
  component: React.ComponentType<any>;
  patterns: string[];
  priority: number;
  serviceType: string;
}

// 智能组件注册表
class SmartComponentRegistry {
  private components: ComponentMetadata[] = [];
  private cache: Map<string, React.ComponentType<any> | null> = new Map();

  register(metadata: ComponentMetadata) {
    this.components.push(metadata);
    // 按优先级排序
    this.components.sort((a, b) => b.priority - a.priority);
    this.cache.clear(); // 清除缓存
  }

  findComponent(serviceName: string, handlerName: string): React.ComponentType<any> | null {
    const cacheKey = `${serviceName}/${handlerName}`;
    
    // 检查缓存
    if (this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey) || null;
    }

    // 查找匹配组件
    for (const meta of this.components) {
      if (this.matchesPattern(serviceName, handlerName, meta.patterns)) {
        this.cache.set(cacheKey, meta.component);
        return meta.component;
      }
    }

    this.cache.set(cacheKey, null);
    return null;
  }

  private matchesPattern(serviceName: string, handlerName: string, patterns: string[]): boolean {
    const fullPath = `${serviceName}/${handlerName}`.toLowerCase();
    const lowerHandler = handlerName.toLowerCase();
    const lowerService = serviceName.toLowerCase();

    return patterns.some(pattern => {
      const lowerPattern = pattern.toLowerCase();
      
      // 精确匹配
      if (lowerPattern === fullPath || lowerPattern === lowerHandler) {
        return true;
      }
      
      // 通配符匹配
      if (lowerPattern.includes('*')) {
        const regex = new RegExp('^' + lowerPattern.replace(/\*/g, '.*') + '$');
        return regex.test(fullPath) || regex.test(lowerHandler);
      }
      
      // 包含匹配
      return fullPath.includes(lowerPattern) || lowerHandler.includes(lowerPattern);
    });
  }

  // 向后兼容的访问方法
  get(key: string): React.ComponentType<any> | null {
    // 尝试解析键名
    const parts = key.split('/');
    if (parts.length >= 2) {
      return this.findComponent(parts[0], parts[parts.length - 1]);
    } else {
      return this.findComponent('', key);
    }
  }

  // 获取所有组件键（向后兼容）
  keys(): string[] {
    const keys = new Set<string>();
    this.components.forEach(meta => {
      meta.patterns.forEach(pattern => {
        keys.add(pattern);
      });
    });
    return Array.from(keys);
  }
}

// 创建智能注册表实例
const smartRegistry = new SmartComponentRegistry();

// 注册所有组件（简化版本，消除重复）
const componentMappings = [
  // 蛋白质基础分析
  {
    component: MolecularWeightResult,
    patterns: ['molecular_weight*', 'protein_analysis/molecular_weight*'],
    priority: 10,
    serviceType: 'protein_analysis'
  },
  {
    component: InstabilityIndexResult,
    patterns: ['instability_index*', 'protein_analysis/instability_index*'],
    priority: 10,
    serviceType: 'protein_analysis'
  },
  {
    component: FlexibilityResult,
    patterns: ['flexibility*', 'protein_analysis/flexibility*'],
    priority: 10,
    serviceType: 'protein_analysis'
  },
  {
    component: ChargeProfileResult,
    patterns: ['charge_profile*', 'protein_analysis/charge_profile*'],
    priority: 10,
    serviceType: 'protein_analysis'
  },
  {
    component: ExtinctionCoefficientResult,
    patterns: ['extinction_coefficient*', 'protein_analysis/extinction_coefficient*'],
    priority: 10,
    serviceType: 'protein_analysis'
  },
  {
    component: HydropathyResult,
    patterns: ['hydropathy*', 'protein_analysis/hydropathy*'],
    priority: 10,
    serviceType: 'protein_analysis'
  },
  {
    component: TMPredictionResult,
    patterns: ['tm_prediction*', 'transmembrane*', 'protein_analysis/tm_prediction*'],
    priority: 10,
    serviceType: 'protein_analysis'
  },
  {
    component: SignalPeptideResult,
    patterns: ['signal_prediction*', 'protein_analysis/signal_prediction*'],
    priority: 10,
    serviceType: 'protein_analysis'
  },
  {
    component: PTMPredictionResult,
    patterns: ['ptm*', 'protein_analysis/ptm*', 'ptm_*'],
    priority: 10,
    serviceType: 'protein_analysis'
  },
  {
    component: DisorderPredictionResult,
    patterns: ['disorder*', 'protein_analysis/disorder*'],
    priority: 10,
    serviceType: 'protein_analysis'
  },
  {
    component: EcoliExpressionResult,
    patterns: ['ecoli_expression*', 'protein_analysis/ecoli_expression*'],
    priority: 10,
    serviceType: 'protein_analysis'
  },
  // 抗体分析
  {
    component: BatchAntibodyResult,
    patterns: ['antibody_numbering*', 'batch_antibody_numbering*', 'findcdr*', 'antibody_analysis/findcdr*', 'cdr_analyze*'],
    priority: 12,
    serviceType: 'antibody_analysis'
  },
  {
    component: CLBTopeResult,
    patterns: ['epitope_prediction*', 'epitope_predict*', 'clbtope*'],
    priority: 11,
    serviceType: 'antibody_analysis'
  },
  {
    component: EpitopePredictionResult,
    patterns: ['bepipred*'],
    priority: 10,
    serviceType: 'antibody_analysis'
  },
  // 抗体粘度预测
  {
    component: ViscosityResult,
    patterns: ['predict_viscosity*', 'antibody_viscosity*'],
    priority: 10,
    serviceType: 'antibody_service'
  },
  // 序列处理 - 六框翻译（优先级高于转换，避免 *_to_* 误匹配）
  {
    component: SixFrameTranslationResult,
    patterns: ['nucleotide_to_protein*', 'batch_nucleotide_to_protein*', 'six_frame*', 'translate*'],
    priority: 12,
    serviceType: 'sequence_processing'
  },
  // 序列处理 - 转换（排除翻译类任务）
  {
    component: SequenceConversionResult,
    patterns: ['convert/*', 'dna_to_rna*', 'rna_to_dna*', 'dna_complement*', 'rna_complement*', 'dna_reverse_complement*', 'rna_reverse_complement*', 'batch_dna_to_rna*', 'batch_rna_to_dna*', 'batch_dna_complement*', 'batch_rna_complement*', 'batch_dna_reverse_complement*', 'batch_rna_reverse_complement*'],
    priority: 8,
    serviceType: 'sequence_processing'
  },
  {
    component: AminoAcidConversionResult,
    patterns: ['amino_acid*'],
    priority: 8,
    serviceType: 'sequence_processing'
  },
  // 分子互作
  {
    component: RNAhybridResult,
    patterns: ['rnahybrid*'],
    priority: 10,
    serviceType: 'molecular_interaction'
  },
  {
    component: ProdigyResult,
    patterns: ['prodigy*'],
    priority: 10,
    serviceType: 'molecular_interaction'
  },
  {
    component: ZdockResult,
    patterns: ['zdock*'],
    priority: 10,
    serviceType: 'molecular_interaction'
  },
  {
    component: MegadockResult,
    patterns: ['megadock*'],
    priority: 10,
    serviceType: 'molecular_interaction'
  },
  {
    component: EquiDockResult,
    patterns: ['equidock*'],
    priority: 10,
    serviceType: 'molecular_interaction'
  },
  // 结构设计
  {
    component: ProteinMPNNResults,
    patterns: ['proteinmpnn*', 'hypermpnn*', 'design_json'],
    priority: 10,
    serviceType: 'structure_design'
  },
  {
    component: ThermoMPNNResult,
    patterns: ['thermompnn*'],
    priority: 10,
    serviceType: 'structure_design'
  },
  // Protenix结构预测
  {
    component: ProtenixResult,
    patterns: ['protenix*'],
    priority: 10,
    serviceType: 'structure_prediction'
  },
  // ESMFold结构预测
  {
    component: ESMFoldResult,
    patterns: ['esmfold*'],
    priority: 10,
    serviceType: 'structure_prediction'
  },
  // 引物设计
  {
    component: PrimerDesignResult,
    patterns: ['primer_design*', 'pcr_design*', 'clone/primer*'],
    priority: 10,
    serviceType: 'clone_service'
  },
  {
    component: SequenceValidationResult,
    patterns: ['batch_validate*', 'validate*', 'sequence_validation*'],
    priority: 10,
    serviceType: 'sequence_processing'
  },
  {
    component: BatchSequenceResult,
    patterns: ['batch_*'],
    priority: 5,
    serviceType: 'sequence_processing'
  },
  {
    component: MultipleSequenceAlignmentResult,
    patterns: ['align*', 'multiple_sequence_alignment*', 'sequence_analysis/align*'],
    priority: 10,
    serviceType: 'sequence_processing'
  },
  {
    component: GeneSynthesisResult,
    patterns: ['gene_synthesis*', 'clone/gene_synthesis*'],
    priority: 10,
    serviceType: 'clone_service'
  },
  {
    component: AntibodyStructureResult,
    patterns: ['antibody_structure*', 'antibody_service/antibody_structure*'],
    priority: 10,
    serviceType: 'antibody_service'
  },
  {
    component: CutMap,
    patterns: ['enzyme_cutmap*', 'cutmap*'],
    priority: 10,
    serviceType: 'bio_charts'
  },
  {
    component: PhylogeneticTreeResult,
    patterns: ['phylogenetic_tree*'],
    priority: 10,
    serviceType: 'bio_charts'
  },
  {
    component: ScatterChart,
    patterns: ['scatter_chart*'],
    priority: 5,
    serviceType: 'bio_charts'
  },
  {
    component: BaseChart,
    patterns: ['base_chart*'],
    priority: 3,
    serviceType: 'bio_charts'
  },
  // 图表
  {
    component: BarChart,
    patterns: ['bar_chart*'],
    priority: 5,
    serviceType: 'bio_charts'
  },
  {
    component: LineChart,
    patterns: ['line_chart*'],
    priority: 5,
    serviceType: 'bio_charts'
  }
];

// 注册所有组件
componentMappings.forEach(mapping => {
  smartRegistry.register(mapping);
});

// 向后兼容接口 - 智能查找 + 兜底硬编码
const componentRegistry = {
  // 智能查找方法（新）
  findComponent: (serviceName: string, handlerName: string) => {
    return smartRegistry.findComponent(serviceName, handlerName);
  },
  
  // 直接访问方法（兼容旧代码）
  get: (key: string) => {
    // 首先尝试智能查找
    const smartResult = smartRegistry.get(key);
    if (smartResult) return smartResult;
    
    // 兜底硬编码映射（关键组件）
    const fallbackMappings: Record<string, React.ComponentType<any>> = {
      // 蛋白质基础分析组件映射
      'molecular_weight_json': MolecularWeightResult,
      'instability_index_json': InstabilityIndexResult,
      'flexibility_json': FlexibilityResult,
      'charge_profile_json': ChargeProfileResult,
      'extinction_coefficient_json': ExtinctionCoefficientResult,
      'hydropathy_json': HydropathyResult,
      'amino_acid_convert_json': AminoAcidConversionResult,
      
      // 蛋白质高级分析组件映射
      'tm_prediction_predict_json': TMPredictionResult,
      'signal_prediction_predict_json': SignalPeptideResult,
      'ptm_predict_json': PTMPredictionResult,
      'ptm_prediction': PTMPredictionResult,  // PTM预测任务组件
      'disorder_predict_json': DisorderPredictionResult,
      'ecoli_expression_predict_json': EcoliExpressionResult,
      
      // 其他关键组件
      'gene_synthesis': GeneSynthesisResult,
      'gene_synthesis_predict_json': GeneSynthesisResult,  // 更新为新的endpoint名称
      'design_primers_json': GeneSynthesisResult,
      'primer_design_predict_json': PrimerDesignResult,
      'antibody_structure': AntibodyStructureResult,
      'antibody_structure_predict_json': AntibodyStructureResult,
      'protenix_predict_json': ProtenixResult,  // Protenix专用组件
      'esmfold_local_predict_json': ESMFoldResult,  // ESMFold专用组件
      'rnahybrid_predict_json': RNAhybridResult,
      // 抗原表位预测（任务页使用 epitope_predict_json）- 使用 CLBTope
      'epitope_predict_json': CLBTopeResult,
      // 抗体粘度预测
      'predict_viscosity_json': ViscosityResult,
      'enzyme_cutmap': CutMap,
      'cutmap': CutMap,
      'phylogenetic_tree': PhylogeneticTreeResult,
      'multiple_sequence_alignment': MultipleSequenceAlignmentResult,
      'align': MultipleSequenceAlignmentResult,
      'validate': SequenceValidationResult,
      'nucleotide_to_protein': SixFrameTranslationResult,
      'batch_validate': BatchSequenceResult,
      'scatter_chart': ScatterChart,
      'base_chart': BaseChart
    };
    
    return fallbackMappings[key] || null;
  },
  
  // 数组访问兼容
  keys: () => smartRegistry.keys(),
  
  // 直接属性访问（废弃警告）
};

export default componentRegistry;